
import { useWebSocketConnection } from "./useWebSocketConnection";
import { useAgentManagement } from "./useAgentManagement";
import { useMessageHandling } from "./useMessageHandling";
import { UseWebSocketChatReturn } from "./types";

interface UseWebSocketChatProps {
  initialAgentId?: string;
}

export const useWebSocketChat = ({ 
  initialAgentId 
}: UseWebSocketChatProps = {}): UseWebSocketChatReturn => {
  
  // Agent management - let it fetch default agent if no initialAgentId
  const {
    selectedAgentId,
    selectedAgent,
    isInitializing,
    changeAgent
  } = useAgentManagement({ initialAgentId });
  
  // WebSocket connection
  const { 
    socket, 
    isConnected, 
    reconnectAttempts, 
    maxReconnectAttempts, 
    connectWebSocket 
  } = useWebSocketConnection({ agentId: selectedAgentId });

  // Message handling
  const {
    messages,
    isLoading: messageLoading,
    sendMessage: baseSendMessage,
    resetMessages
  } = useMessageHandling({ socket, selectedAgentId });

  // Enhanced sendMessage with reconnection logic
  const sendMessage = (text: string) => {
    if (!text.trim()) return;
    
    if (socket && socket.readyState === WebSocket.OPEN) {
      baseSendMessage(text);
    } else {
      baseSendMessage(text); // This will handle the error case
      
      // Only try to reconnect if not already connected and not already trying to reconnect
      if (!isConnected && reconnectAttempts === 0) {
        connectWebSocket();
      }
    }
  };

  // Enhanced changeAgent with message reset
  const handleChangeAgent = (agentId: string, agent: any) => {
    changeAgent(agentId, agent);
    resetMessages();
  };

  // If there's an initialization error, show it as loading with error
  const finalIsLoading = messageLoading || isInitializing;

  return {
    messages,
    isLoading: finalIsLoading,
    isConnected,
    reconnectAttempts,
    sendMessage,
    maxReconnectAttempts,
    selectedAgentId,
    selectedAgent,
    changeAgent: handleChangeAgent
  };
};
